import { Injectable, signal } from '@angular/core';
import { ICredentials, IDataLogin, IUsuarioLogin } from '../interfaces/usuario.interface';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, tap } from 'rxjs';
import { ACCESS_TOKEN, API_URL, PATH_LOGIN, REFRESH_TOKEN, USER_DATA } from '../constants';
import { IResultadoApi } from '../interfaces/app.inteface';
import { IAccessModule, IAccessResponse } from '../interfaces/menu.interface';
import { ToastService } from '../components/toast/toast.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  isLoggedIn = signal<boolean>(false);
  user = signal<IUsuarioLogin>({});
  token = signal<any>(null);
  accessData = signal<IAccessResponse | null>(null);

  apiURL: string = `${API_URL}/auth`;

  constructor(
    private router: Router,
    private http: HttpClient,
    private toast: ToastService
  ) { }


  login(credentials: ICredentials): Observable<IDataLogin> {
    return this.http.post<IDataLogin>(`${this.apiURL}/login`, credentials);
  }

  recover(credentials: ICredentials): Observable<IDataLogin> {
    return this.http.post<IDataLogin>(`${this.apiURL}/forgot-password`, credentials);
  }

  getAccesos(): Observable<IAccessResponse> {
    return this.http.get<IAccessResponse>(`${API_URL}/access/`);
  }

  loadAccess(): Observable<IAccessResponse | null> {
    return this.getAccesos().pipe(
      tap(response => this.accessData.set(response)),
      catchError((error) => {
        this.toast.danger('No tienes acceso a ningún módulo.');
        this.logout();
        return of(null);
      })
    );
  }

  hasAccess(slug: string): boolean {
    return this.checkPermission(slug, 'canRead');
  }

  canCreate(slug: string): boolean {
    return this.checkPermission(slug, 'canCreate');
  }

  canUpdate(slug: string): boolean {
    return this.checkPermission(slug, 'canUpdate');
  }

  canDelete(slug: string): boolean {
    return this.checkPermission(slug, 'canDelete');
  }

  canDownload(slug: string): boolean {
    return this.checkPermission(slug, 'canDownload');
  }

  private checkPermission(slug: string, permission: keyof Pick<IAccessModule, 'canRead' | 'canCreate' | 'canUpdate' | 'canDelete' | 'canDownload'>): boolean {
    const access = this.accessData();
    if (!access || !access.modules) return false;

    const normalizedSlug = slug.startsWith('/dashboard') ? slug.replace('/dashboard', '') : slug;

    // Retorna el valor del permiso si encuentra el módulo, null si no aplica
    const findModule = (modules: IAccessModule[]): boolean | null => {
      for (const mod of modules) {
        // 1. Coincidencia exacta: tiene prioridad máxima
        if (normalizedSlug === mod.slug) {
          return mod[permission];
        }
        // 2. Primero recorrer submodulos para buscar coincidencia más específica
        if (mod.submodules && mod.submodules.length > 0) {
          const childResult = findModule(mod.submodules);
          if (childResult !== null) return childResult;
        }
        // 3. Fallback: el slug pertenece a una ruta más profunda de este módulo
        if (normalizedSlug.startsWith(mod.slug + '/')) {
          return mod[permission];
        }
      }
      return null;
    };

    return findModule(access.modules) ?? false;
  }

  getFirstAllowedUrl(): string | null {
    const access = this.accessData();
    if (!access || !access.modules) return null;
    const findFirstUrl = (modules: IAccessModule[]): string | null => {
      const sortedModules = [...modules].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      for (const mod of sortedModules) {
        if (mod.canRead) {
          if (mod.submodules && mod.submodules.length > 0) {
            const childUrl = findFirstUrl(mod.submodules);
            if (childUrl) return childUrl;
          }
          return mod.slug;
        }
      }
      return null;
    };
    return findFirstUrl(access.modules);
  }

  reset(credentials: ICredentials): Observable<IResultadoApi> {
    return this.http.post<IResultadoApi>(`${this.apiURL}/reset-password`, credentials);
  }

  support(code: string): Observable<IDataLogin> {
    return this.http.post<IDataLogin>(`${API_URL}/auth/support/handoffs/redeem`, { code });
  }

  /** Refresca el token */
  refreshToken(refreshToken: string): Observable<IDataLogin> {
    return this.http.post<IDataLogin>(`${this.apiURL}/refresh`, { refreshToken: refreshToken });
  }

  /** 
  * Crea el usuario y el token en localstorage
  * @param data: contiene la respuesta enviada por el login desde el backend
  */
  setSession(accessToken: string, refreshToken: string, recordarme: boolean = true) {
    if (accessToken && refreshToken) {
      const payload = this.parseJwt(accessToken);
      this.user.set(payload);
      if (recordarme) {
        const userData = {
          id: payload.id,
          exp: payload.exp,
          email: payload.email,
          name: payload.name,
          sub: payload.sub
        }
        localStorage.setItem(USER_DATA, JSON.stringify(userData));
      }
      this.setTokens(accessToken, refreshToken);
    }
  }

  parseJwt(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  }

  /** Crea los tokens en localStorage */
  setTokens(accessToken: string, refreshToken: string) {
    localStorage.setItem(ACCESS_TOKEN, accessToken);
    localStorage.setItem(REFRESH_TOKEN, refreshToken);
  }

  /** Comprueba si existe el Token */
  hasToken() {
    return !!localStorage.getItem(ACCESS_TOKEN);
  }

  /** Comprueba si existe el RefreshToken */
  hasRefreshToken() {
    return !!localStorage.getItem(REFRESH_TOKEN);
  }

  /** Retorna el Token desde localStorage */
  getAccessToken(): string {
    return localStorage.getItem(ACCESS_TOKEN) || '';
  }

  /** Retorna el Token desde localStorage */
  getRefreshToken(): string {
    return localStorage.getItem(REFRESH_TOKEN) || '';
  }

  /** Obtiene el usaurio desde localStorage */
  getUserSesion() {
    try {
      const user = JSON.parse(localStorage.getItem(USER_DATA) || 'null');
      if (user?.exp && user?.sub) return user;
    } catch {
    }
    return null;
  }

  /** Comprueba si el token aun es válido */
  isValidToken(token: string): boolean {
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp > Date.now() / 1000;
    } catch (error) {
      return false;
    }
  }

  /** Elimina la sesión actual */
  logout() {
    this.destroySession();
    this.router.navigate([PATH_LOGIN]);
    this.accessData.set(null);
  }

  destroySession() {
    localStorage.clear();
    this.isLoggedIn.set(false);
    this.user.set({})
  }

  /** Verifica si existen los datos en localstorage */
  verifyLogin(redirect: boolean = true) {
    if (this.hasToken() && this.hasRefreshToken()) {
      this.isLoggedIn.set(true);
      if (redirect) {
        this.router.navigate(['/dashboard']);
      }
      return true;
    } else {
      this.isLoggedIn.set(false);
      this.logout();
      this.router.navigate([PATH_LOGIN]);
      return false;
    }
  }

  isAuthenticated(): boolean {
    return this.hasToken() && this.hasRefreshToken() && !!this.getUserSesion();
  }

}
